/**
 * TAREAS → CITAS MEDICAS — la lista de toda la agencia (punto 10, fase 2).
 *
 * En Apizeal esta lista cuelga de "Tareas y Recordatorios", al lado de Tareas y
 * Recordatorios, y es la pantalla desde la que de verdad se trabaja: se llama
 * al cliente, se cuelga y se marca el estatus sin abrir nada. Aqui igual — el
 * chip del estatus ES el desplegable.
 *
 * La tarjeta y el formulario son los MISMOS que los de la ficha
 * (`CitasMedicas.jsx`): dos copias acabarian enseñando cosas distintas en cada
 * sitio.
 *
 * ── LO QUE ESTA LISTA NO PUEDE HACER, Y LO DICE ─────────────────────────────
 *
 * El buscador busca por NOMBRE DE CLIENTE y nada mas. La razon, el proveedor y
 * la direccion estan cifrados fila a fila, asi que buscar dentro de ellos
 * obligaria a descifrar la agencia entera en memoria en cada tecla. El marcador
 * del buscador lo dice, en vez de devolver cero y dejar creyendo que no hay.
 *
 * Y de aqui NO se cambia de persona ni de medico: esas dos listas son de la
 * ficha del cliente. Se dice en el formulario y se ofrece el enlace.
 */
import { useState, useMemo } from 'react';
import { CalendarPlus, Search, RefreshCw } from 'lucide-react';
import {
  TarjetaDeCita, FormularioDeCita, useCitasMedicas, citaComoFormulario,
} from './CitasMedicas.jsx';
import { ESTATUS_DE_CITA, ESTATUS, etiquetaDeEstatus } from '../citasMedicas.js';

/** Un instante local -> ISO, sin pasar por `toISOString()` de una fecha suelta. */
const isoDe = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();

/**
 * Los rangos de fecha que se ofrecen.
 *
 * "Próximas" es el que sale de fábrica: quien abre esto pregunta qué viene, no
 * qué pasó. Las fechas se calculan con el DÍA LOCAL — con `toISOString()` sobre
 * medianoche, en Florida el rango empieza el día anterior a las 8 pm.
 */
const RANGOS = [
  { id: 'proximas', es: 'Próximas', en: 'Upcoming' },
  { id: 'semana', es: 'Esta semana', en: 'This week' },
  { id: 'mes', es: 'Este mes', en: 'This month' },
  { id: 'pasadas', es: 'Ya pasadas', en: 'Past' },
  { id: 'todas', es: 'Todas', en: 'All' },
];

function fechasDelRango(id) {
  const ahora = new Date();
  const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const finDelDia = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
  if (id === 'proximas') return { from: isoDe(hoy), to: '' };
  if (id === 'pasadas') return { from: '', to: isoDe(new Date(hoy.getTime() - 1000)) };
  if (id === 'semana') {
    const fin = new Date(hoy); fin.setDate(fin.getDate() + 7);
    return { from: isoDe(hoy), to: isoDe(finDelDia(fin)) };
  }
  if (id === 'mes') {
    const fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    return { from: isoDe(hoy), to: isoDe(finDelDia(fin)) };
  }
  return { from: '', to: '' };
}

export default function CitasMedicasView({ lang = 'es', onAbrirCliente }) {
  const es = lang !== 'en';
  const [rango, setRango] = useState('proximas');
  const [estatus, setEstatus] = useState([]);   // vacío = todos
  const [texto, setTexto] = useState('');

  const filtros = useMemo(() => {
    const { from, to } = fechasDelRango(rango);
    return { from, to, status: estatus.join(','), q: texto.trim() };
  }, [rango, estatus, texto]);

  const { citas, cargando, errorDeCarga, hayMas, guardando, cambiando, cargar, guardar, borrar, cambiarEstatus } =
    useCitasMedicas({ lang, filtros });

  const [form, setForm] = useState(null);
  const [editando, setEditando] = useState(null);
  const cerrar = () => { setForm(null); setEditando(null); };
  const abrirEdicion = (cita) => { setEditando(cita); setForm(citaComoFormulario(cita)); };

  const alternarEstatus = (id) => {
    setEstatus((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Las citas ya vienen ordenadas por fecha del servidor; aquí solo se parten
  // por día para poder poner una cabecera legible.
  const porDia = useMemo(() => {
    const mapa = new Map();
    for (const c of citas) {
      const d = new Date(c.startsAt);
      const clave = Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
      if (!mapa.has(clave)) mapa.set(clave, []);
      mapa.get(clave).push(c);
    }
    return [...mapa.entries()];
  }, [citas]);

  return (
    <div style={{ padding: '1rem 1.1rem', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.2rem' }}>
        <CalendarPlus size={18} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
        <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>
          {es ? 'Citas médicas' : 'Medical appointments'}
        </h2>
        <button type="button" onClick={cargar} title={es ? 'Actualizar' : 'Refresh'} className="dkt-btn-s"
          style={{ marginLeft: 'auto', fontSize: '.72rem', padding: '.3rem .6rem', display: 'flex', alignItems: 'center', gap: 5 }}>
          <RefreshCw size={12} /> {es ? 'Actualizar' : 'Refresh'}
        </button>
      </div>
      <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '.9rem' }}>
        {es
          ? 'Las citas médicas de todos los clientes. El estatus se cambia desde la propia lista. Se crean y se editan por completo desde la ficha del cliente.'
          : 'Every client’s medical appointments. The status changes right here. They are created and fully edited from the client record.'}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '.75rem' }}>
        <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>
          {RANGOS.map((r) => (
            <button key={r.id} type="button" onClick={() => setRango(r.id)}
              style={{ padding: '.28rem .65rem', borderRadius: 100, fontSize: '.7rem', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', border: '1px solid',
                background: rango === r.id ? 'rgba(0,212,255,.12)' : 'transparent',
                borderColor: rango === r.id ? 'rgba(0,212,255,.35)' : 'var(--c-border,#1a3550)',
                color: rango === r.id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)' }}>
              {es ? r.es : r.en}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text3,#45688a)' }} />
          <input value={texto} onChange={(e) => setTexto(e.target.value)}
            placeholder={es ? 'Buscar por nombre de cliente…' : 'Search by client name…'}
            className="dkt-inp" style={{ paddingLeft: 28, width: '100%', boxSizing: 'border-box' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap', marginBottom: '.9rem' }}>
        {ESTATUS_DE_CITA.map((id) => {
          const activo = estatus.includes(id);
          const color = ESTATUS[id].color;
          return (
            <button key={id} type="button" onClick={() => alternarEstatus(id)}
              style={{ padding: '.22rem .6rem', borderRadius: 100, fontSize: '.68rem', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', border: '1px solid',
                background: activo ? `${color}1a` : 'transparent',
                borderColor: activo ? `${color}66` : 'var(--c-border,#1a3550)',
                color: activo ? color : 'var(--c-text3,#45688a)' }}>
              {ESTATUS[id].icono} {etiquetaDeEstatus(id, lang)}
            </button>
          );
        })}
        {!!estatus.length && (
          <button type="button" onClick={() => setEstatus([])}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontFamily: 'inherit' }}>
            {es ? 'quitar filtro' : 'clear filter'}
          </button>
        )}
      </div>

      {cargando && (
        <div style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--c-text3,#45688a)', fontSize: '.82rem' }}>
          {es ? 'Cargando…' : 'Loading…'}
        </div>
      )}

      {!!errorDeCarga && !cargando && (
        <div style={{ padding: '.75rem 1rem', borderRadius: 10, background: 'rgba(255,61,107,.08)',
          border: '1px solid rgba(255,61,107,.25)', color: 'var(--c-red,#ff3d6b)', fontSize: '.8rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.75rem' }}>
          <span>{errorDeCarga}</span>
          <button type="button" onClick={cargar} className="dkt-btn-s" style={{ fontSize: '.72rem', padding: '.3rem .75rem' }}>
            {es ? 'Reintentar' : 'Retry'}
          </button>
        </div>
      )}

      {!cargando && !errorDeCarga && !citas.length && (
        <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--c-text3,#45688a)',
          background: 'var(--c-surface2,#112538)', borderRadius: 12, border: '1px dashed var(--c-border,#1a3550)' }}>
          <div style={{ fontSize: '2rem', marginBottom: '.4rem' }}>📅</div>
          <div style={{ fontSize: '.85rem', color: 'var(--c-text,#c8dff0)', marginBottom: '.3rem' }}>
            {es ? 'No hay citas con este filtro' : 'No appointments with this filter'}
          </div>
          <div style={{ fontSize: '.73rem' }}>
            {es
              ? 'Las citas se crean desde la ficha del cliente, en la pestaña Médicos y citas.'
              : 'Appointments are created from the client record, on the Doctors & visits tab.'}
          </div>
        </div>
      )}

      {!cargando && !errorDeCarga && porDia.map(([dia, delDia]) => (
        <div key={dia} style={{ marginBottom: '.9rem' }}>
          <div style={{ fontSize: '.62rem', color: 'var(--c-text3,#45688a)', fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: '.35rem' }}>
            {dia} · {delDia.length}
          </div>
          <div style={{ background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, overflow: 'hidden' }}>
            {delDia.map((c) => (
              <div key={c.id} style={{ position: 'relative' }}>
                <TarjetaDeCita cita={c} lang={lang} mostrarCliente
                  onEditar={abrirEdicion} onBorrar={borrar} cambiando={cambiando[c.id]}
                  onEstatus={(s) => cambiarEstatus(c, s)} />
                {!!onAbrirCliente && !!c.clientId && (
                  <button type="button" onClick={() => onAbrirCliente(c.clientId)}
                    style={{ position: 'absolute', right: '.85rem', bottom: '.6rem', background: 'none', border: 'none',
                      cursor: 'pointer', fontSize: '.68rem', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)', fontFamily: 'inherit' }}>
                    {es ? 'Abrir ficha →' : 'Open record →'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {hayMas && (
        <div style={{ fontSize: '.72rem', color: 'var(--c-amber,#ffb700)', marginTop: '.5rem' }}>
          ⚠️ {es
            ? 'La lista se cortó: hay más citas de las que caben. Afina el rango de fechas o el estatus.'
            : 'The list was cut: there are more appointments than fit. Narrow the date range or the status.'}
        </div>
      )}

      {!!form && (
        <FormularioDeCita
          titulo={`📅 ${es ? 'Editar cita' : 'Edit appointment'}`}
          valor={form} personas={[]} medicos={[]} lang={lang} guardando={guardando}
          onCambiar={setForm} onCerrar={cerrar}
          onGuardar={async () => { if (await guardar(form, editando, editando?.clientId)) cerrar(); }} />
      )}
    </div>
  );
}
